import { useCallback, useEffect, useRef, useState } from "react";

const ICE_SERVERS = [
    {
        urls: ["stun:stun.l.google.com:19302", "stun:global.stun.twilio.com:3478"],
    },
];

const DISCONNECTED_GRACE_MS = 8000;

export default function useAudioCall({ wsRef, currentUserId }) {
    const [callState, setCallState] = useState("idle");
    const [incomingCall, setIncomingCall] = useState(null);
    const [activeCall, setActiveCall] = useState(null);
    const [error, setError] = useState("");

    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const remoteStreamRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const pendingCandidatesRef = useRef([]);
    const activeCallRef = useRef(null);
    const incomingCallRef = useRef(null);
    const disconnectTimerRef = useRef(null);

    const lastRemoteAnswerSdpRef = useRef("");
    const lastRemoteOfferSdpRef = useRef("");

    const syncActiveCall = useCallback((valueOrUpdater) => {
        setActiveCall((prev) => {
            const next =
                typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
            activeCallRef.current = next;
            return next;
        });
    }, []);

    const syncIncomingCall = useCallback((valueOrUpdater) => {
        setIncomingCall((prev) => {
            const next =
                typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
            incomingCallRef.current = next;
            return next;
        });
    }, []);

    const clearDisconnectTimer = useCallback(() => {
        if (disconnectTimerRef.current) {
            clearTimeout(disconnectTimerRef.current);
            disconnectTimerRef.current = null;
        }
    }, []);

    const stopTracks = useCallback((stream) => {
        if (!stream) return;
        stream.getTracks().forEach((track) => {
            try {
                track.stop();
            } catch { }
        });
    }, []);

    const resetSignalCaches = useCallback(() => {
        lastRemoteAnswerSdpRef.current = "";
        lastRemoteOfferSdpRef.current = "";
        pendingCandidatesRef.current = [];
    }, []);

    const resetCallState = useCallback(() => {
        syncIncomingCall(null);
        syncActiveCall(null);
        setCallState("idle");
    }, [syncIncomingCall, syncActiveCall]);

    const cleanupPeer = useCallback(
        ({ resetState = false, clearErr = false } = {}) => {
            clearDisconnectTimer();

            try {
                if (pcRef.current) {
                    pcRef.current.ontrack = null;
                    pcRef.current.onicecandidate = null;
                    pcRef.current.onconnectionstatechange = null;
                    pcRef.current.close();
                }
            } catch { }

            pcRef.current = null;

            stopTracks(localStreamRef.current);
            localStreamRef.current = null;

            stopTracks(remoteStreamRef.current);
            remoteStreamRef.current = null;

            if (remoteAudioRef.current) {
                remoteAudioRef.current.srcObject = null;
            }

            resetSignalCaches();

            if (clearErr) {
                setError("");
            }

            if (resetState) {
                resetCallState();
            }
        },
        [clearDisconnectTimer, stopTracks, resetSignalCaches, resetCallState]
    );

    const endWithCleanup = useCallback(
        (nextState = "ended", nextError = "", options = {}) => {
            const { clearErr = false } = options;

            if (nextError) {
                setError(nextError);
            } else if (clearErr) {
                setError("");
            }

            cleanupPeer({ resetState: false, clearErr: false });
            syncIncomingCall(null);
            syncActiveCall(null);
            setCallState(nextState);

            if (nextState === "ended") {
                window.setTimeout(() => {
                    setCallState((prev) => (prev === "ended" ? "idle" : prev));
                    if (clearErr || !nextError) {
                        setError("");
                    }
                }, 800);
            }
        },
        [cleanupPeer, syncIncomingCall, syncActiveCall]
    );

    const ensureLocalAudio = useCallback(async () => {
        if (localStreamRef.current) return localStreamRef.current;

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
        });

        localStreamRef.current = stream;
        return stream;
    }, []);

    const flushPendingCandidates = useCallback(async () => {
        const pc = pcRef.current;
        if (!pc || !pc.remoteDescription) return;

        const candidates = [...pendingCandidatesRef.current];
        pendingCandidatesRef.current = [];

        for (const candidate of candidates) {
            try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (err) {
                console.error("Failed to add pending ICE candidate:", err);
            }
        }
    }, []);

    const createPeerConnection = useCallback(
        ({ convId, otherUserId }) => {
            if (pcRef.current) return pcRef.current;

            const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
            pcRef.current = pc;

            remoteStreamRef.current = new MediaStream();
            if (remoteAudioRef.current) {
                remoteAudioRef.current.srcObject = remoteStreamRef.current;
            }

            pc.ontrack = (event) => {
                const stream = event.streams?.[0];
                if (!stream || !remoteStreamRef.current) return;

                stream.getTracks().forEach((track) => {
                    const exists = remoteStreamRef.current
                        .getTracks()
                        .some((t) => t.id === track.id);

                    if (!exists) {
                        remoteStreamRef.current.addTrack(track);
                    }
                });
            };

            pc.onicecandidate = (event) => {
                if (!event.candidate || !wsRef?.current) return;

                try {
                    wsRef.current.webrtcSignal({
                        convId,
                        to: otherUserId,
                        kind: "ice-candidate",
                        candidate: event.candidate,
                        callType: "audio",
                    });
                } catch (err) {
                    console.error("Failed to send ICE candidate:", err);
                }
            };

            pc.onconnectionstatechange = () => {
                const state = pc.connectionState;

                if (state === "connected") {
                    clearDisconnectTimer();
                    setError("");
                    setCallState("connected");
                    return;
                }

                if (state === "disconnected") {
                    clearDisconnectTimer();
                    disconnectTimerRef.current = window.setTimeout(() => {
                        if (pcRef.current?.connectionState === "disconnected") {
                            endWithCleanup("ended", "Call disconnected");
                        }
                    }, DISCONNECTED_GRACE_MS);
                    return;
                }

                if (state === "failed") {
                    endWithCleanup("ended", "Call connection failed");
                    return;
                }

                if (state === "closed") {
                    endWithCleanup("ended", "", { clearErr: true });
                }
            };

            return pc;
        },
        [wsRef, clearDisconnectTimer, endWithCleanup]
    );

    const startAudioCall = useCallback(
        async ({
            convId,
            to,
            propertyId,
            visitorId,
            sessionId,
            userType,
            userId,
            userIcon,
            userName,
            pageName,
            pageURL,
        }) => {
            setError("");
            resetSignalCaches();

            if (!wsRef?.current) {
                const msg = "Socket instance missing";
                setError(msg);
                return { ok: false, reason: msg };
            }

            if (!wsRef.current.isReady?.()) {
                const msg = "Socket not connected";
                setError(msg);
                return { ok: false, reason: msg };
            }

            const payload = {
                convId,
                to,
                propertyId,
                visitorId,
                sessionId,
                userType,
                userId,
                userIcon,
                userName,
                pageName,
                pageURL,
            };

            let ok = false;

            try {
                ok = wsRef.current.inviteAudioCall(payload);
            } catch (err) {
                const msg = err?.message || "Unable to send call invite";
                setError(msg);
                console.error("inviteAudioCall failed:", err, payload);
                return { ok: false, reason: msg };
            }

            if (!ok) {
                const msg = "Unable to send call invite";
                setError(msg);
                return { ok: false, reason: msg };
            }

            syncActiveCall({
                callId: "",
                callType: "audio",
                convId,
                from: currentUserId,
                to,
                propertyId,
                visitorId,
                sessionId,
                userType,
                userId,
                userIcon,
                userName,
                pageName,
                pageURL,
            });

            setCallState("outgoing");
            return { ok: true };
        },
        [wsRef, currentUserId, resetSignalCaches, syncActiveCall]
    );

    const acceptIncomingCall = useCallback(async () => {
        const call = incomingCallRef.current;
        if (!call) return;

        try {
            setError("");
            setCallState("connecting");
            syncActiveCall(call);
            syncIncomingCall(null);

            const stream = await ensureLocalAudio();
            const pc = createPeerConnection({
                convId: call.convId,
                otherUserId: call.from,
            });

            stream.getTracks().forEach((track) => {
                const already = pc.getSenders().find((sender) => sender.track?.id === track.id);
                if (!already) {
                    pc.addTrack(track, stream);
                }
            });

            wsRef?.current?.acceptAudioCall({
                callId: call.callId,
                convId: call.convId,
                to: call.from,
            });
        } catch (err) {
            console.error("Failed to accept audio call:", err);
            setError(err?.message || "Failed to accept audio call");
            cleanupPeer({ resetState: false });
            syncIncomingCall(null);
            syncActiveCall(null);
            setCallState("idle");
        }
    }, [
        ensureLocalAudio,
        createPeerConnection,
        wsRef,
        cleanupPeer,
        syncIncomingCall,
        syncActiveCall,
    ]);

    const rejectIncomingCall = useCallback(() => {
        const call = incomingCallRef.current;
        if (!call) return;

        try {
            wsRef?.current?.rejectAudioCall({
                callId: call.callId,
                convId: call.convId,
                to: call.from,
                reason: "rejected",
            });
        } catch (err) {
            console.error("Failed to reject audio call:", err);
        }

        syncIncomingCall(null);
        setError("");
        setCallState("idle");
    }, [wsRef, syncIncomingCall]);

    const endCall = useCallback(() => {
        const call = activeCallRef.current || incomingCallRef.current;
        const otherUserId = call?.from === currentUserId ? call?.to : call?.from;

        try {
            if (call?.convId && otherUserId) {
                wsRef?.current?.endAudioCall({
                    callId: call?.callId || "",
                    convId: call.convId,
                    to: otherUserId,
                });
            }
        } catch (err) {
            console.error("Failed to signal endAudioCall:", err);
        }

        endWithCleanup("ended", "", { clearErr: true });
    }, [currentUserId, wsRef, endWithCleanup]);

    const handleSocketEvent = useCallback(
        async (type, data) => {
            if (!data) return;

            try {
                if (type === "call:incoming") {
                    if (data?.callType !== "audio") return;
                    syncIncomingCall(data);
                    setCallState("incoming");
                    return;
                }

                if (type === "call:outgoing") {
                    if (data?.callType !== "audio") return;
                    syncActiveCall(data);
                    setCallState("outgoing");
                    return;
                }

                if (type === "call:accepted") {
                    const currentCall = activeCallRef.current || data;
                    if ((currentCall?.callType || data?.callType) !== "audio") return;

                    const pcExisting = pcRef.current;

                    syncActiveCall((prev) => ({
                        ...(prev || {}),
                        ...(currentCall || {}),
                        ...data,
                        callId: data.callId || prev?.callId || "",
                        callType: "audio",
                    }));

                    if (pcExisting && pcExisting.signalingState === "have-local-offer") {
                        console.warn("Ignoring duplicate call:accepted event");
                        return;
                    }

                    setCallState("connecting");

                    const stream = await ensureLocalAudio();
                    const pc = createPeerConnection({
                        convId: currentCall.convId || data.convId,
                        otherUserId: data.from,
                    });

                    stream.getTracks().forEach((track) => {
                        const already = pc.getSenders().find((sender) => sender.track?.id === track.id);
                        if (!already) {
                            pc.addTrack(track, stream);
                        }
                    });

                    if (pc.signalingState !== "stable") {
                        console.warn(
                            "Skipping offer creation because signalingState is not stable:",
                            pc.signalingState
                        );
                        return;
                    }

                    const offer = await pc.createOffer({
                        offerToReceiveAudio: true,
                        offerToReceiveVideo: false,
                    });

                    await pc.setLocalDescription(offer);

                    wsRef?.current?.webrtcSignal({
                        convId: currentCall.convId || data.convId,
                        to: data.from,
                        kind: "offer",
                        sdp: offer,
                        callType: "audio",
                    });
                    return;
                }

                if (type === "call:rejected") {
                    const currentCall = activeCallRef.current || incomingCallRef.current || data;
                    if ((currentCall?.callType || data?.callType) !== "audio") return;
                    endWithCleanup("ended", "Call rejected");
                    return;
                }

                if (type === "call:ended") {
                    const currentCall = activeCallRef.current || incomingCallRef.current || data;
                    if ((currentCall?.callType || data?.callType) !== "audio") return;
                    endWithCleanup("ended", "", { clearErr: true });
                    return;
                }

                if (type !== "webrtc:signal") return;
                if (data?.callType && data.callType !== "audio") return;

                const kind = data?.kind;

                if (kind === "offer") {
                    const incomingOfferSdp = data?.sdp?.sdp || "";
                    if (
                        incomingOfferSdp &&
                        incomingOfferSdp === lastRemoteOfferSdpRef.current
                    ) {
                        console.warn("Ignoring duplicate remote offer");
                        return;
                    }

                    const stream = await ensureLocalAudio();
                    const pc = createPeerConnection({
                        convId: data.convId,
                        otherUserId: data.from,
                    });

                    if (pc.signalingState !== "stable") {
                        console.warn("Ignoring offer because signalingState is:", pc.signalingState);
                        return;
                    }

                    stream.getTracks().forEach((track) => {
                        const already = pc.getSenders().find((sender) => sender.track?.id === track.id);
                        if (!already) {
                            pc.addTrack(track, stream);
                        }
                    });

                    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
                    lastRemoteOfferSdpRef.current = incomingOfferSdp;

                    await flushPendingCandidates();

                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);

                    wsRef?.current?.webrtcSignal({
                        convId: data.convId,
                        to: data.from,
                        kind: "answer",
                        sdp: answer,
                        callType: "audio",
                    });

                    setCallState("connecting");
                    return;
                }

                if (kind === "answer") {
                    const pc = pcRef.current;
                    if (!pc) return;

                    const incomingAnswerSdp = data?.sdp?.sdp || "";

                    if (
                        incomingAnswerSdp &&
                        incomingAnswerSdp === lastRemoteAnswerSdpRef.current
                    ) {
                        console.warn("Ignoring duplicate remote answer");
                        return;
                    }

                    if (pc.signalingState !== "have-local-offer") {
                        console.warn(
                            "Ignoring remote answer because signalingState is:",
                            pc.signalingState
                        );
                        return;
                    }

                    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
                    lastRemoteAnswerSdpRef.current = incomingAnswerSdp;

                    await flushPendingCandidates();
                    return;
                }

                if (kind === "ice-candidate") {
                    if (!data.candidate) return;

                    if (!pcRef.current || !pcRef.current.remoteDescription) {
                        pendingCandidatesRef.current.push(data.candidate);
                        return;
                    }

                    try {
                        await pcRef.current.addIceCandidate(
                            new RTCIceCandidate(data.candidate)
                        );
                    } catch (err) {
                        console.error("Failed to add live ICE candidate:", err);
                    }
                }
            } catch (err) {
                console.error("Audio call socket handler failed:", err, { type, data });
                setError(err?.message || "Audio call failed");
            }
        },
        [
            ensureLocalAudio,
            createPeerConnection,
            flushPendingCandidates,
            wsRef,
            endWithCleanup,
            syncIncomingCall,
            syncActiveCall,
            cleanupPeer,
        ]
    );

    useEffect(() => {
        return () => {
            cleanupPeer({ resetState: false, clearErr: true });
        };
    }, [cleanupPeer]);

    return {
        callState,
        incomingCall,
        activeCall,
        error,
        remoteAudioRef,
        startAudioCall,
        acceptIncomingCall,
        rejectIncomingCall,
        endCall,
        handleSocketEvent,
        clearError: () => setError(""),
    };
}